import type { DossierEntity } from "./types";

export interface FinanceResponse {
  finalGlobalRole?: string | null;
  growthPercent?: number | string | null;
  monthlyGrowthPercent?: number | string | null;
  jsonData?: unknown;
}
export interface FinanceTrendPoint {
  month: string;
  revenueMsek: number;
  volumeMkg: number;
}

export function financeItemNumber(item: DossierEntity): string | null {
  const field = item.fields.ItemNumber;
  if (field?.status !== "available") return null;
  const value = field.rawValue;
  return typeof value === "string" && value.trim() ? value.trim() :
    typeof value === "number" && Number.isFinite(value) ? String(value) : null;
}

export function financeNumber(value: unknown): number | null {
  if (typeof value !== "number" && typeof value !== "string") return null;
  if (typeof value === "string" && !value.trim()) return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

export function growthText(value: unknown): string {
  const number = financeNumber(value);
  if (number === null) return "—";
  return `${number > 0 ? "+" : ""}${number.toFixed(2).replace(/0$/, "")}%`;
}

const normalize = (key: string) => key.replace(/[^a-z0-9]/gi, "").toLowerCase();
const named = (object: Record<string, unknown>, key: string) =>
  Object.entries(object).find(([name]) => normalize(name) === key)?.[1];

export function financeTrend(value: unknown): FinanceTrendPoint[] {
  let decoded = value;
  for (let depth = 0; depth < 3; depth++) {
    if (typeof decoded === "string") {
      try { decoded = JSON.parse(decoded); } catch { return []; }
      continue;
    }
    if (!decoded || typeof decoded !== "object" || Array.isArray(decoded)) return [];
    const object = decoded as Record<string, unknown>;
    if (Array.isArray(named(object, "header")) && Array.isArray(named(object, "data"))) break;
    decoded = named(object, "jsondata");
  }
  if (!decoded || typeof decoded !== "object" || Array.isArray(decoded)) return [];
  const object = decoded as Record<string, unknown>;
  const rawHeader = named(object, "header"), rows = named(object, "data");
  if (!Array.isArray(rawHeader) || !Array.isArray(rows)) return [];
  const header = rawHeader.map(key => normalize(String(key)));
  const revenueIndex = header.indexOf("totalrevenuesek"), volumeIndex = header.indexOf("totalvolumekg");
  if (revenueIndex < 0 || volumeIndex < 0) return [];
  const points: FinanceTrendPoint[] = [];
  for (const row of rows) {
    if (!Array.isArray(row)) continue;
    const date = row[header.indexOf("reportingmonthstart")];
    const parsed = typeof date === "string" && Number.isFinite(Date.parse(date)) ? /^(\d{4})-(\d{2})/.exec(date) : null;
    const year = parsed ? Number(parsed[1]) : financeNumber(row[header.indexOf("year")]);
    const month = parsed ? Number(parsed[2]) : financeNumber(row[header.indexOf("month")]);
    const revenue = financeNumber(row[revenueIndex]), volume = financeNumber(row[volumeIndex]);
    if (year === null || month === null || !Number.isInteger(year) || !Number.isInteger(month) || year < 1 || year > 9999 || month < 1 || month > 12 || revenue === null || volume === null) continue;
    points.push({ month: `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}`, revenueMsek: revenue / 1_000_000, volumeMkg: volume / 1_000_000 });
  }
  return points.sort((a, b) => a.month.localeCompare(b.month));
}
